import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserSettingsHttpService } from '../user-settings';
import { SelectOption } from '../../views/partials/content/general/searchable-select/searchable-select.component';

export interface FilterParams {
	disti_id: string | number | (string | number)[] | null;
	market_id: string | number | (string | number)[] | null;
	client_id: string | number | (string | number)[] | null;
	campus_id: string | number | (string | number)[] | null;
	machine_id?: string | number | (string | number)[] | null;
}

@Injectable({ providedIn: 'root' })
export class FilterOptionsService {
	constructor(
		private userSettingsHttp: UserSettingsHttpService
	) {}

	/**
	 * Load distributors based on user role
	 */
	loadDistributors(role: string, includeAll: boolean = false): Observable<SelectOption[]> {
		if (role !== 'sadmin') {
			return of([]);
		}
		return this.userSettingsHttp.getDistis().pipe(
			map((distis: any[]) => {
				const options = distis.map((d: any) => ({
					value: d.disti_id,
					label: d.name,
					...d
				}));

				if (includeAll && options.length > 0) {
					options.unshift({ value: null, label: 'All' });
				}

				return options;
			})
		);
	}

	/**
	 * Normalize ID parameter to comma-separated string for API
	 */
	private normalizeIdParam(id: string | number | (string | number)[] | null): string | null {
		if (!id) {
			return null;
		}
		if (Array.isArray(id)) {
			if (id.length === 0) {
				return null;
			}
			return id.map(i => String(i)).join(',');
		}
		return String(id);
	}

	/**
	 * Load markets for a given distributor(s)
	 */
	loadMarkets(distributorId: string | number | (string | number)[] | null, includeAll: boolean = false): Observable<SelectOption[]> {
		const normalizedId = this.normalizeIdParam(distributorId);
		if (!normalizedId) {
			return of([]);
		}
		return this.userSettingsHttp.getMarkets({ disti_id: normalizedId }).pipe(
			map((markets: any[]) => {
				const options = markets.map((m: any) => ({
					value: m.market_id,
					label: m.market_name,
					...m
				}));

				if (includeAll && options.length > 0) {
					options.unshift({ value: null, label: 'All' });
				}

				return options;
			})
		);
	}

	/**
	 * Load clients for given distributor(s) and/or market(s)
	 */
	loadClients(
		distributorId: string | number | (string | number)[] | null,
		marketId: string | number | (string | number)[] | null,
		includeAll: boolean = false,
		retailOnly: boolean = false,
		retailEquipment: boolean = false
	): Observable<SelectOption[]> {
		const params: Record<string, string> = {};
		const normalizedDistiId = this.normalizeIdParam(distributorId);
		if (normalizedDistiId) {
			params.disti_id = normalizedDistiId;
		}
		const normalizedMarketId = this.normalizeIdParam(marketId);
		if (normalizedMarketId) {
			params.market_id = normalizedMarketId;
		}
		if (retailOnly) {
			params.retail_only = '1';
		}
		if (retailEquipment) {
			params.retail_equipment = '1';
		}

		return this.userSettingsHttp.getClients(params).pipe(
			map((clients: any[]) => {
				const options = clients.map((c: any) => ({
					value: c.client_id,
					label: c.company_name,
					...c
				}));

				if (includeAll && options.length > 0) {
					options.unshift({ value: null, label: 'All' });
				}

				return options;
			})
		);
	}

	/**
	 * Load campuses for given client(s), distributor(s), and/or market(s)
	 */
	loadCampuses(
		clientId: string | number | (string | number)[] | null,
		distributorId: string | number | (string | number)[] | null,
		marketId: string | number | (string | number)[] | null,
		includeAll: boolean = false,
		retailOnly: boolean = false,
		retailEquipment: boolean = false
	): Observable<SelectOption[]> {
		const params: Record<string, string> = {};
		const normalizedClientId = this.normalizeIdParam(clientId);
		if (normalizedClientId) {
			params.client_id = normalizedClientId;
		}
		const normalizedDistiId = this.normalizeIdParam(distributorId);
		if (normalizedDistiId) {
			params.disti_id = normalizedDistiId;
		}
		const normalizedMarketId = this.normalizeIdParam(marketId);
		if (normalizedMarketId) {
			params.market_id = normalizedMarketId;
		}
		if (retailOnly) {
			params.retail_only = '1';
		}
		if (retailEquipment) {
			params.retail_equipment = '1';
		}

		return this.userSettingsHttp.getCampus(params).pipe(
			map((campuses: any[]) => {
				const options = campuses.map((c: any) => ({
					value: c.campus_id,
					label: c.name,
					...c
				}));

				if (includeAll && options.length > 0) {
					options.unshift({ value: null, label: 'All' });
				}

				return options;
			})
		);
	}

	/**
	 * Load machines for given campus(es), client(s), distributor(s), and/or market(s)
	 */
	loadMachines(
		campusId: string | number | (string | number)[] | null,
		clientId: string | number | (string | number)[] | null,
		distributorId: string | number | (string | number)[] | null,
		marketId: string | number | (string | number)[] | null,
		includeAll: boolean = false,
		retailEquipment: boolean = false
	): Observable<SelectOption[]> {
		const params: any = {};
		if (retailEquipment) {
			params.retail_equipment = '1';
		}

		const normalizedCampusId = this.normalizeIdParam(campusId);
		if (normalizedCampusId) {
			params.campus_id = normalizedCampusId;
		}
		const normalizedClientId = this.normalizeIdParam(clientId);
		if (normalizedClientId) {
			params.client_id = normalizedClientId;
		}
		const normalizedDistiId = this.normalizeIdParam(distributorId);
		if (normalizedDistiId) {
			params.disti_id = normalizedDistiId;
		}
		const normalizedMarketId = this.normalizeIdParam(marketId);
		if (normalizedMarketId) {
			params.market_id = normalizedMarketId;
		}

		return this.userSettingsHttp.getMachines(params).pipe(
			map((machines: any[]) => {
				const options = (machines || []).map((m: any) => ({
					value: m.machine_id,
					label: m.machine_name || m.serial_no || m.machine_id,
					...m
				}));

				if (includeAll && options.length > 0) {
					options.unshift({ value: null, label: 'All' });
				}

				return options;
			})
		);
	}
}

